<template>
    <page :tabs="tabs" :busy="busy">
        <grid
            ref="grid"
            class="inquiry_roi"
            :columnDefs="columnDefs"
            :rowData="rowData"
            :isAutoWidth="true"
            :isAutoHeight="true"
            :isSearch="false"
            gridTheme="ag-theme-alpine-report"
        >
            <date-range-picker
                :rangeOptions="predefinedRanges"
                :value="dateRange"
                :dateFormat="userUnicodeDateFormat"
                :disableCustomRange="true"
                @input="handleDaterangeChange"
            />
            <div class="d-flex align-items-baseline mr-1 mb-1 filter-properties">
                <form-search-multiselect
                    v-model="filterProperties"
                    :options="filterPropertyOptions"
                    :icon="['fas', 'hotel']"
                    :responsiveness="true"
                    variant="primary"
                    placeholder="Filter Properties"
                />
            </div>
            <export-to-excel-button
                :href="exportExcelHref"
            ></export-to-excel-button>
            <report-settings-modal
                :modalId="reportSettingsModalId"
                :initialValues="reportSettings"
                :excelOnly="true"
            />
        </grid>
        <grid
            class="h-100 inquiry_monthly"
            ref="gridMonthly"
            :isSearch="false"
            :isAutoWidth="true"
            :isAutoHeight="true"
            :columnDefs="columnDefsMonthly"
            :rowData="rowDataMonthly"
            :getRowStyle="getRowStyle"
            :headerHeight="45"
            gridTheme="ag-theme-alpine-report"
        />
    </page>
</template>

<script>
import { mapGetters } from 'vuex';
import format from 'date-fns/format';
import API from '../../api';
import Page from '../generic/page/page.vue';
import Grid from '../generic/grid/grid.vue';
import ExportToExcelButton from '../generic/export-to-excel-button/export-to-excel-button.vue';
import FormSearchMultiselect from '../generic/form-search-multiselect/form-search-multiselect.vue';
import DateRangePicker, {
    nameToRange,
    LAST_N_YEARS_0,
    LAST_N_YEARS_1,
    LAST_N_YEARS_2,
    LAST_N_YEARS_3,
    LAST_N_YEARS_4
} from '../generic/date-range-picker/date-range-picker.vue';
import {
    REPORT_INQUIRY_ROI_DATA_ENDPOINT,
    REPORT_INQUIRY_ROI_EXCEL_ENDPOINT,
    REPORT_INQUIRY_ROI_PROPERTIES_ENDPOINT,
} from '../../endpoints';
import { removeScrollMixin } from '../../mixins/remove-scroll-mixin';
import { toAmountDisplay } from '../../utils/numbers';
import ReportSettingsModal, {
        MULTI_SELECT_INPUT,
        DATE_PERIODS_INPUT,
} from './report-settings/report-settings-modal.vue';

export default {
    name: 'InquiryROIReport',
    components: {
        Page,
        Grid,
        DateRangePicker,
        ExportToExcelButton,
        FormSearchMultiselect,
        ReportSettingsModal
    },
    mixins: [removeScrollMixin],
    data() {
        return {
            title: 'Inquiry ROI Report',
            tabs: [
                {
                    title: 'Inquiry ROI Report',
                    route: '/app/reports/inquiry-roi/',
                    active: true,
                },
            ],
            dateRange: nameToRange(LAST_N_YEARS_0),
            predefinedRanges: [
                LAST_N_YEARS_4,
                LAST_N_YEARS_3,
                LAST_N_YEARS_2,
                LAST_N_YEARS_1,
                LAST_N_YEARS_0,
            ],
            dateRequestFormat: 'yyyy-MM-dd',
            busy: false,
            rowData: [],
            rowDataMonthly: [],
            getRowStyle: null,
            filterProperties: [],
            filterPropertyOptions: [],
            reportSettingsModalId: 'report-settings-modal',
        };
    },
    computed: {
        ...mapGetters('header', ['userUnicodeDateFormat']),
        columnDefs() {
            return [
                {
                    field: 'source',
                    headerName: 'Inquiry Source',
                    minWidth: 120,
                },
                {
                    field: 'cost',
                    headerName: 'Cost',
                    type: 'amountColumn',
                    minWidth: 100,
                },
                {
                    field: 'total_inquiries',
                    headerName: 'Total Inquiries',
                    minWidth: 160,
                },
                {
                    headerName: '% of Total Inquiries',
                    field: 'percent_total_inquiries',
                    minWidth: 160,
                    valueGetter: ({ data }) => {
                        return toAmountDisplay({
                            amount: data.percent_total_inquiries || 0,
                            percentage: true
                        });
                    }
                },
                {
                    headerName: 'Bookings',
                    field: 'bookings',
                    minWidth: 100,
                },
                {
                    field: '',
                    headerName: '',
                    autoWidthExclude: true,
                    sortable: false,
                },
            ];
        },
        columnDefsMonthly() {
            return [
                {
                    field: 'month',
                    headerName: 'Month',
                    minWidth: 100,
                },
                ...this.rowData.map((item, index) => {
                    return {
                        headerName: item.source,
                        field: `field_${index}`,
                        minWidth: 100,
                    }
                }),
                {
                    field: 'total',
                    headerName: 'Total',
                    minWidth: 80,
                },
                {
                    field: '',
                    headerName: '',
                    autoWidthExclude: true,
                    sortable: false,
                },
            ]
        },
        exportExcelHref() {
            let url = REPORT_INQUIRY_ROI_EXCEL_ENDPOINT();
            url += `?from_date=${this.startDateRequest}`;
            url += `&to_date=${this.endDateRequest}`;
            url += `&properties=${this.filterProperties.join(',')}`;
            return url;
        },
        startDateRequest() {
            return format(this.dateRange.from, this.dateRequestFormat);
        },
        endDateRequest() {
            return format(this.dateRange.to, this.dateRequestFormat);
        },
        reportSettings() {
            return {
                    name: 'Inquiry ROI',
                    report_sysname: 'INQUIRY_ROI',
                    settings: [
                        {
                            type: DATE_PERIODS_INPUT,
                            predefinedRanges: this.predefinedRanges,
                            value: LAST_N_YEARS_0,
                            label: 'Date Period',
                            name: 'from_date',
                        },
                        {
                            type: MULTI_SELECT_INPUT,
                            value: [],
                            label: 'Only for Properties',
                            placeholder: 'All Properties',
                            name: 'properties',
                            options: this.filterPropertyOptions,
                        },
                    ],
            };
        },
    },
    mounted() {
        this.fetchProperties();
        this.fetchData();
    },
    beforeMount() {
        this.getRowStyle = (params) => {
            if (params.data.month === 'Subtotal:') {
                return { 'font-weight': 'bold' };
            }
        };
    },
    methods: {
        async fetchData() {
            try {
                this.busy = true;
                const { data } = await API.get(
                    REPORT_INQUIRY_ROI_DATA_ENDPOINT(),
                    {
                        params: {
                            from_date: this.startDateRequest,
                            to_date: this.endDateRequest,
                            properties: this.filterProperties.join(','),
                        }
                    }
                );
                this.rowData = data.roi_info.map((item, index) => ({...item, id: index}))
                this.rowDataMonthly = data.monthly_info.info.map((item, index) => {
                    const month = item[0];
                    const total = item[item.length - 1];
                    const fields = item.slice(1, item.length - 1)
                    const row = {
                        id: index,
                        month,
                        total,
                    }
                    fields.forEach((field, fieldIndex) => {
                        row[`field_${fieldIndex}`] = field
                    })
                    return row
                })
                const totalRow = {
                    id: this.rowDataMonthly.length,
                    month: 'Subtotal:',
                    total: data.monthly_info.total[data.monthly_info.total.length - 1]
                }
                data.monthly_info.total.slice(0, data.monthly_info.total.length - 1).forEach((field, fieldIndex) => {
                    totalRow[`field_${fieldIndex}`] = field
                })
                this.rowDataMonthly.push(totalRow)
            } catch (e) {
                console.error(e)
                this.errorToast({ text: "Could not fetch data" });
            } finally {
                this.busy = false
            }
        },
        async fetchProperties() {
            const { data } = await API.get(REPORT_INQUIRY_ROI_PROPERTIES_ENDPOINT());
            this.filterPropertyOptions = data
                .map((item) => ({ value: item.property_id, text: item.name, disabled: item.disabled }))

        },
        handleDaterangeChange(value) {
            this.dateRange = { ...value };
            this.fetchData();
        },
    },
    watch: {
        filterProperties: {
            handler() {
                this.fetchData();
            },
            deep: true,
        }
    }
};
</script>

<style lang="scss" scoped>
@import '~bootstrap/scss/_functions.scss';
@import '~bootstrap/scss/_variables.scss';

.inquiry_roi {
    display: inline;
}
.inquiry_monthly {
    display: inline;
    padding-left: 1px;
}
.filter-properties {
    > fieldset {
        margin: 0 !important;
    }
}
</style>
